import { useRef, useState, type KeyboardEvent } from 'react';
import { X } from 'lucide-react';
import { LIMITS } from '../../core/data/limits';
import { canonicalSkill, cleanList } from '../../core/text';
import { cn } from '../../lib/cn';

export interface SkillsInputProps {
  skills: string[];
  onChange: (skills: string[]) => void;
}

/**
 * Skills as removable chips.
 *
 * The obvious alternative — one comma-separated text input bound to
 * `skills.join(', ')` — fights the user: the moment they type a comma the value
 * is reparsed and rejoined, so the cursor jumps and a trailing space vanishes.
 * Committing on Enter, comma, or blur avoids that entirely, and chips make the
 * parsed result visible, which matters because LinkedIn treats each skill as a
 * separate searchable entity.
 *
 * Pasting is handled too: a pasted block of "React, TypeScript, SQL" splits into
 * three chips, since that is how people get their skills out of LinkedIn.
 */
export function SkillsInput({ skills, onChange }: SkillsInputProps) {
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const atLimit = skills.length >= LIMITS.skills.hard;

  const commit = (raw: string) => {
    const incoming = cleanList(raw.split(/[,\n;]/));
    if (incoming.length === 0) {
      setDraft('');
      return;
    }
    // Deduplicate against what is already there, comparing canonically so
    // 'Node.js' does not join a list that already has 'nodejs'.
    const seen = new Set(skills.map(canonicalSkill));
    const added: string[] = [];
    for (const skill of incoming) {
      const key = canonicalSkill(skill);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      added.push(skill);
    }
    if (added.length > 0) onChange([...skills, ...added].slice(0, LIMITS.skills.hard));
    setDraft('');
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      commit(draft);
      return;
    }
    // Backspace on an empty field removes the last chip — the behaviour people
    // expect from every other tag input they have used.
    if (event.key === 'Backspace' && draft === '' && skills.length > 0) {
      event.preventDefault();
      onChange(skills.slice(0, -1));
    }
  };

  const remove = (index: number) => {
    onChange(skills.filter((_, i) => i !== index));
    inputRef.current?.focus();
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor="skills-input" className="text-sm font-medium text-strong">
          Skills
        </label>
        <span
          className={cn(
            'text-xs tabular-nums',
            skills.length < LIMITS.skills.minRecommended
              ? 'text-important-700 dark:text-important-400'
              : 'text-subtle'
          )}
        >
          {skills.length} / {LIMITS.skills.hard}
        </span>
      </div>

      <p id="skills-hint" className="text-xs leading-relaxed text-muted">
        Press Enter or comma after each one. Your first {LIMITS.skills.pinned} are the ones people
        see without clicking, so put the ones you want to be found for there — aim for{' '}
        {LIMITS.skills.goodCount}+ in total.
      </p>

      <div
        className={cn(
          'flex flex-wrap items-center gap-1.5 rounded-field border border-line bg-card p-2',
          'focus-within:border-brand-500 focus-within:shadow-ring'
        )}
        onClick={() => inputRef.current?.focus()}
      >
        {skills.map((skill, index) => (
          <span
            key={`${skill}-${index}`}
            className={cn(
              'inline-flex items-center gap-1 rounded-pill py-0.5 pr-1 pl-2.5 text-xs font-medium',
              // The first three are visually distinct because LinkedIn pins them.
              index < LIMITS.skills.pinned
                ? 'bg-brand-100 text-brand-800 dark:bg-brand-900 dark:text-brand-100'
                : 'bg-sunken text-muted'
            )}
          >
            {skill}
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                remove(index);
              }}
              className="rounded-pill p-0.5 opacity-60 transition-opacity hover:opacity-100"
            >
              <span className="sr-only-live">Remove {skill}</span>
              <X aria-hidden="true" className="size-3" />
            </button>
          </span>
        ))}

        <input
          ref={inputRef}
          id="skills-input"
          value={draft}
          disabled={atLimit}
          aria-describedby="skills-hint"
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={onKeyDown}
          // Commit on blur so a half-typed skill is not silently discarded when
          // the user tabs away or clicks Analyse.
          onBlur={() => commit(draft)}
          placeholder={
            atLimit
              ? `You have hit LinkedIn's limit of ${LIMITS.skills.hard}`
              : skills.length === 0
                ? 'Python, SQL, Data Visualisation…'
                : 'Add another…'
          }
          className="min-w-40 flex-1 bg-transparent px-1.5 py-1 text-sm text-strong placeholder:text-subtle focus:outline-none disabled:cursor-not-allowed"
        />
      </div>
    </div>
  );
}
